import {
  IconClock,
  IconCpu,
  IconFileDollar,
  IconFileSettings,
  IconForms,
  IconHomePlus,
  IconInbox,
  IconInfoCircle,
  IconPackage,
  IconPlaneDeparture,
  IconShoppingCart,
  IconUsersGroup,
} from '@tabler/icons-react';
import {
  ALL_APP_IDS,
  ALL_FEATURE_IDS,
  ALL_VIEW_IDS,
} from 'constants/appConstants';
import ExtendedTabs from 'ui-component/extended/ExtendedTabs';
import OrgInfo from 'views/org/OrgInfo';
import DateTimePreferences from 'views/org/Preferences/DateTimePreferences';
import PartPreferences from 'views/org/Preferences/PartPreferences';
import SubOrgs from 'views/org/SubOrgs';
import CustomPropertiesTable from 'views/properties/CustomProperties/CustomPropertiesTable';
import TeamPreferences from 'views/properties/Teams/TeamPreferences';
import InventoryPreferences from 'views/org/Preferences/InventoryPreferences';
import OrderPreferences from 'views/purchasing/OrderPreferences';
import ProcurementPreferences from 'views/procurement/Preferences';
import DocAiExtractors from 'views/docai/Extractors';
import DocAiPreferences from 'views/docai/Preferences';
import ShippingPreferences from 'views/inventory/ShippingPreferences';
import MainCard from 'ui-component/cards/MainCard';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';

export enum ORG_PREFERENCES_TABS {
  INFO = 'info',
  DATE_TIME = 'dateTime',
  PARTS = 'parts',
  TEAMS = 'teams',
  INBOUND_EMAIL = 'inboundEmail',
  SHIPPING = 'shipping',
  INVENTORY = 'inventory',
  PURCHASING = 'purchasing',
  ORDERS = 'orders',
  SUB_ORGS = 'subOrgs',
  DOCAI_EXTRACTORS = 'docaiExtractors',
  CUSTOM_PROPERTIES = 'customProperties',
}

const OrgPreferences = () => {
  const { defaultTeamTypeNamePlural } = useCustomTeamTypeName();

  return (
    <ExtendedTabs
      tabOptions={[
        {
          name: ORG_PREFERENCES_TABS.INFO,
          label: 'Info',
          icon: <IconInfoCircle />,
          contents: <OrgInfo />,
        },
        {
          name: ORG_PREFERENCES_TABS.DATE_TIME,
          label: 'Date Time',
          icon: <IconClock />,
          contents: <DateTimePreferences />,
        },
        {
          name: ORG_PREFERENCES_TABS.PARTS,
          label: 'Parts',
          icon: <IconCpu />,
          contents: <PartPreferences />,
          permissionScope: {
            app: ALL_APP_IDS.PARTS,
            feature: ALL_FEATURE_IDS.CUSTOM_PNS,
          },
        },
        {
          name: ORG_PREFERENCES_TABS.TEAMS,
          label: defaultTeamTypeNamePlural,
          icon: <IconUsersGroup />,
          contents: <TeamPreferences />,
          permissionScope: {
            app: ALL_APP_IDS.PROPERTIES,
            view: ALL_VIEW_IDS.TEAMS_TABLE,
          },
        },
        {
          name: ORG_PREFERENCES_TABS.INBOUND_EMAIL,
          label: 'Inbound Email',
          icon: <IconInbox />,
          contents: <DocAiPreferences />,
          permissionScope: {
            app: ALL_APP_IDS.DOCAI,
            view: ALL_VIEW_IDS.DOC_AI_INBOX,
          },
        },
        {
          name: ORG_PREFERENCES_TABS.SHIPPING,
          label: 'Shipping',
          icon: <IconPlaneDeparture />,
          contents: <ShippingPreferences />,
          permissionScope: {
            app: ALL_APP_IDS.KITTING,
          },
        },
        {
          name: ORG_PREFERENCES_TABS.INVENTORY,
          label: 'Inventory',
          icon: <IconPackage />,
          contents: <InventoryPreferences />,
          permissionScope: {
            app: ALL_APP_IDS.STOCK,
          },
        },
        {
          name: ORG_PREFERENCES_TABS.PURCHASING,
          label: 'Purchasing',
          icon: <IconFileDollar />,
          contents: <ProcurementPreferences />,
          permissionScope: {
            app: ALL_APP_IDS.PROCUREMENT,
          },
        },
        {
          name: ORG_PREFERENCES_TABS.ORDERS,
          label: 'Orders',
          icon: <IconShoppingCart />,
          contents: <OrderPreferences />,
          permissionScope: {
            app: ALL_APP_IDS.ORDERS,
          },
        },
        {
          name: ORG_PREFERENCES_TABS.SUB_ORGS,
          label: 'Sub Orgs',
          icon: <IconHomePlus />,
          contents: <SubOrgs />,
          permissionScope: {
            app: ALL_APP_IDS.ORG_SETTINGS,
            feature: ALL_FEATURE_IDS.SUB_ORGS,
          },
        },
        {
          name: ORG_PREFERENCES_TABS.DOCAI_EXTRACTORS,
          label: 'DocAI Extractors',
          icon: <IconFileSettings />,
          contents: (
            <MainCard>
              <DocAiExtractors />
            </MainCard>
          ),
          permissionScope: {
            app: ALL_APP_IDS.DOCAI,
          },
        },
        {
          name: ORG_PREFERENCES_TABS.CUSTOM_PROPERTIES,
          label: 'Custom Properties',
          icon: <IconForms />,
          contents: (
            <MainCard>
              <CustomPropertiesTable />
            </MainCard>
          ),
        },
      ]}
      orientation="vertical"
      displayChildren
      queryParam="tab"
    />
  );
};

export default OrgPreferences;
